import React, { useCallback, useEffect, useState } from 'react';
import { ActionType } from '@common/enums/command-hub/command-buttons';
import MainContentBlock, { PAGE_DIVIDER } from '../ui/MainContentBlock';
import {
  DashboardIcon,
  MissionsIcon,
  MapIcon,
  SettingsIcon,
  UsersIcon,
  SchedulerIcon,
  MapConfigurationIcon,
  RoomOverviewIcon,
  MissionHistoryIcon,
} from '@common/utils/icons/icons';
import RobotInfo from './RobotInfo';
import RobotPageTitle from './RobotPageTitle';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectRobotsData, selectSelectedRobotId } from '../../store/robots/robots-slice-selectors';
import { resetCurrentRobot, setSelectedRobotId } from '../../store/robots/robots-slice';
import MissionsPage from '../missions/MissionsPage';
import RobotContactPerson from './RobotContactPerson';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getLocalRobotDataStateById, getRobotById } from '../../store/robots/robots-actions';
import WebSocketConnectionStatus from '../../models/enums/websocket-connection-status';
import MessageType from '@common/enums/robot/rsw/message-types';
import RobotSettingsPage from './settings/RobotSettingsPage';
import MapPlannerPage from './interaction/room/MapPlannerPage';
import { selectFloorsRestSet } from 'store/local-robot/local-robot-slice-selectors';
import { getFloorsByRobotId } from 'store/local-robot/local-robot-actions';
import SchedulerPage from './interaction/room/SchedulerPage';
import RoomOverviewPage from './interaction/room/RoomOverviewPage';
import MapConfigurationPage from './interaction/room/MapConfigurationPage';
import MissionHistoryPage from 'components/missions/MissionHistoryPage';
import SphereRobotTabs from '@common/enums/query-params/sphere-robot-tabs';
import {
  connectRobotWebsocket,
  disconnectRobotWebsocket,
  sendRobotWebsocketMessage,
} from 'store/middleware/robot-websocket-middleware';
import InteractionAnnouncementPage from './interaction/room/InteractionAnnouncementPage';
import NewReleasesRoundedIcon from '@mui/icons-material/NewReleasesRounded';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectWebappHasUnsyncedChanges } from '@common/store/app-configurations/app-configurations-selectors';
import { resetLocalRobotState } from 'store/local-robot/local-robot-slice';
import { resetLocalRobotConfigurationState } from 'store/local-robot-configuration/local-robot-configuration-slice';
import { isVersionAGreaterThanB } from '@common/utils/version-util';
import { isTabRoomRelated, TAB_QUERY_STRING } from 'utils/url-tabs-helper';
import TorizonUpdateStatus from '../../models/enums/torizon-update-status';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

const RobotPage: React.FC = () => {
  const { t } = useTranslation();

  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [filterParams, setFilterParams] = useSearchParams();
  const queryRobotId = filterParams.get(FilterKeys.RobotId);
  const queryTab = filterParams.get(FilterKeys.Tab);
  const queryRoomId = filterParams.get(FilterKeys.RoomId);

  const dispatch = useAppDispatch();

  const selectedRobotId = useAppSelector((state) => selectSelectedRobotId(state));
  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const floorsRestSet = useAppSelector((state) => selectFloorsRestSet(state));
  const webappHasUnsyncedChanges = useSharedSelector((state) =>
    selectWebappHasUnsyncedChanges(state),
  );

  const robot = robotsData.currentRobot;

  const [isSliderOpen, setIsSliderOpen] = useState(queryRobotId != null);

  useEffect(() => {
    if (
      robotsData.rest.getRobotInformationStatus === HttpFetchStatus.Idle &&
      queryRobotId != null
    ) {
      dispatch(getRobotById(queryRobotId));
    }
  }, [dispatch, queryRobotId, robotsData.rest.getRobotInformationStatus]);

  useEffect(() => {
    setIsSliderOpen(queryRobotId != null);
  }, [queryRobotId]);

  useEffect(() => {
    if (queryRobotId !== selectedRobotId) {
      dispatch(setSelectedRobotId(queryRobotId));
    }
  }, [dispatch, queryRobotId, selectedRobotId]);

  useEffect(() => {
    if (robotsData.rest.getLocalRobotDataStatus === HttpFetchStatus.Idle && queryRobotId != null) {
      dispatch(getLocalRobotDataStateById(queryRobotId));
    }
  }, [dispatch, queryRobotId, robotsData.rest.getLocalRobotDataStatus]);

  useEffect(() => {
    if (floorsRestSet.getStatus === HttpFetchStatus.Idle && queryRobotId != null) {
      dispatch(getFloorsByRobotId(queryRobotId));
    }
  }, [dispatch, floorsRestSet.getStatus, queryRobotId]);

  useEffect(() => {
    if (!webappHasUnsyncedChanges) {
      dispatch(resetLocalRobotState());
      dispatch(resetLocalRobotConfigurationState());
    }
  }, [webappHasUnsyncedChanges, dispatch]);

  useEffect(() => {
    if (
      robotsData.currentRobotData.localDataState?.hasCompletedFullDataSync &&
      !robotsData.currentRobotData.localDataState?.webappHasUnsyncedChanges &&
      queryTab === SphereRobotTabs.InteractionAnnouncement
    ) {
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.Tab, SphereRobotTabs.MapPlanner);
        return prevState;
      });
      window.location.reload();
    }
  }, [
    queryTab,
    robotsData.currentRobotData.localDataState?.hasCompletedFullDataSync,
    robotsData.currentRobotData.localDataState?.webappHasUnsyncedChanges,
    setFilterParams,
  ]);

  useEffect(() => {
    if (
      robot?.status?.roomId != null &&
      queryRoomId == null &&
      queryTab !== null &&
      isTabRoomRelated(queryTab)
    ) {
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.RoomId, robot.status!.roomId!);
        return prevState;
      });
    }
  }, [robot?.status?.roomId, queryRoomId, setFilterParams, robot?.status, queryTab]);

  const dashboardClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.MissionId);
      prevState.delete(FilterKeys.StartDate);
      prevState.delete(FilterKeys.EndDate);
      prevState.delete(FilterKeys.RoomId);
      prevState.set(FilterKeys.Tab, SphereRobotTabs.Dashboard);
      return prevState;
    });
  };

  const navigateTabClickHandler = useCallback(
    (tab: SphereRobotTabs) => {
      setFilterParams((prevState) => {
        if (tab !== SphereRobotTabs.History) {
          prevState.delete(FilterKeys.StartDate);
          prevState.delete(FilterKeys.EndDate);
        }
        if (!isTabRoomRelated(tab)) {
          prevState.delete(FilterKeys.RoomId);
        }
        prevState.set(FilterKeys.Tab, tab);
        return prevState;
      });
    },
    [setFilterParams],
  );

  useEffect(() => {
    if (queryRobotId != null && robot?.status != null) {
      dispatch(connectRobotWebsocket(queryRobotId));
    }
  }, [dispatch, queryRobotId, robot?.status]);

  useEffect(() => {
    return () => {
      dispatch(disconnectRobotWebsocket());
    };
  }, [dispatch]);

  useEffect(() => {
    if (robotsData.robotHubState === WebSocketConnectionStatus.CONNECTED && robot?.status?.active) {
      dispatch(
        sendRobotWebsocketMessage({
          type: MessageType.Actions,
          message: { action: ActionType.ListActions },
        }),
      );
      dispatch(
        sendRobotWebsocketMessage({
          type: MessageType.GetLiveStatus,
          message: null,
        }),
      );
    }
  }, [robot?.status?.active, robotsData.robotHubState, dispatch]);

  const closeOverlayClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.Page);
      prevState.delete(FilterKeys.RobotId);
      prevState.delete(FilterKeys.MissionId);
      prevState.delete(FilterKeys.StartDate);
      prevState.delete(FilterKeys.EndDate);
      prevState.delete(FilterKeys.RoomId);
      prevState.delete(FilterKeys.Tab);
      prevState.delete(TAB_QUERY_STRING);
      return prevState;
    });
    setIsSliderOpen(false);
    dispatch(resetCurrentRobot());
  };

  const isDeploymentActive = robot != null && robot.deploymentDates.end == null;

  useEffect(() => {
    if (
      robot != null &&
      robot.deploymentDates.end != null &&
      queryTab != null &&
      queryTab != SphereRobotTabs.History &&
      Object.values(SphereRobotTabs)
        .map((v) => v.toString())
        .includes(queryTab)
    ) {
      navigateTabClickHandler(SphereRobotTabs.History);
    }
  }, [navigateTabClickHandler, queryTab, robot]);

  if (!isSliderOpen) return null;

  const isRobotInfoLoading =
    robotsData.rest.getRobotInformationStatus === HttpFetchStatus.Idle ||
    robotsData.rest.getRobotInformationStatus === HttpFetchStatus.Loading;
  const hasRobotInfoFailed = robotsData.rest.getRobotInformationStatus === HttpFetchStatus.Failed;

  const getTitle = (subTitle?: string) => {
    return (
      <RobotPageTitle
        robot={robotsData.currentRobot}

        pageName={subTitle}
        isUpdating={robot?.status?.torizonUpdateStatus === TorizonUpdateStatus.IN_PROGRESS}

        isLoading={isRobotInfoLoading}
        hasFailed={hasRobotInfoFailed}
      />
    );
  };

  const robotVersion = robot?.softwareVersion?.rswSoftwareVersion;

  const pages: React.ComponentProps<typeof MainContentBlock>['pages'] = [
    {
      open:
        queryTab === SphereRobotTabs.History ||
        queryTab == null ||
        (!isDeploymentActive && queryTab !== SphereRobotTabs.Settings),
      content: <MissionHistoryPage />,
      tooltip: t('button-history'),
      title: getTitle(t('button-history')),
      icon: <MissionHistoryIcon />,
      onClick: () => navigateTabClickHandler(SphereRobotTabs.History),
    },
  ];

  if (isDeploymentActive) {
    pages.push({
      open: queryTab === SphereRobotTabs.Dashboard,
      content: (
        <RobotInfo
          onRoomNavigateClick={() => navigateTabClickHandler(SphereRobotTabs.MapPlanner)}
        />
      ),
      tooltip: t('button-dashboard'),
      title: getTitle(),
      icon: <DashboardIcon />,
      onClick: dashboardClickHandler,
    });
    pages.push(PAGE_DIVIDER);

    if (robotsData.currentRobotData.localDataState?.hasCompletedFullDataSync != null) {
      if (robotsData.currentRobotData.localDataState?.hasCompletedFullDataSync) {
        pages.push({
          open: queryTab === SphereRobotTabs.Scheduler,
          content: <SchedulerPage />,
          tooltip: t('navigation-scheduler'),
          title: getTitle(t('navigation-scheduler')),
          icon: <SchedulerIcon />,
          onClick: () => navigateTabClickHandler(SphereRobotTabs.Scheduler),
        });
        pages.push({
          open: queryTab === SphereRobotTabs.Missions,
          content: <MissionsPage />,
          tooltip: t('button-missions'),
          title: getTitle(t('button-missions')),
          icon: <MissionsIcon />,
          onClick: () => navigateTabClickHandler(SphereRobotTabs.Missions),
        });
        pages.push({
          open: queryTab === SphereRobotTabs.MapPlanner,
          content: <MapPlannerPage />,
          tooltip: t('navigation-room-planner'),
          title: getTitle(t('navigation-room-planner')),
          icon: <MapIcon />,
          onClick: () => navigateTabClickHandler(SphereRobotTabs.MapPlanner),
        });
        pages.push({
          open: queryTab === SphereRobotTabs.MapConfiguration,
          content: <MapConfigurationPage />,
          tooltip: t('navigation-room-configuration'),
          title: getTitle(t('navigation-room-configuration')),
          icon: <MapConfigurationIcon />,
          onClick: () => navigateTabClickHandler(SphereRobotTabs.MapConfiguration),
        });
        pages.push({
          open: queryTab === SphereRobotTabs.RoomOverview,
          content: <RoomOverviewPage />,
          tooltip: t('navigation-floor-configuration'),
          title: getTitle(t('navigation-floor-configuration')),
          icon: <RoomOverviewIcon />,
          onClick: () => navigateTabClickHandler(SphereRobotTabs.RoomOverview),
        });
        pages.push(PAGE_DIVIDER);
      } else if (robotVersion != null && !isVersionAGreaterThanB('2025.2.0', robotVersion)) {
        pages.push({
          open: queryTab === SphereRobotTabs.InteractionAnnouncement,
          content: <InteractionAnnouncementPage />,
          tooltip: matchesSmDown ? t('button-more') : t('interaction-announcement-title'),
          title: getTitle(matchesSmDown ? t('button-more') : t('interaction-announcement-title')),
          icon: <NewReleasesRoundedIcon color="success" />,
          onClick: () => navigateTabClickHandler(SphereRobotTabs.InteractionAnnouncement),
        });
        pages.push(PAGE_DIVIDER);
      }
    }
    pages.push({
      open: queryTab === SphereRobotTabs.Users,
      content: <RobotContactPerson />,
      tooltip: t('title-robot-contact-people'),
      title: getTitle(t('title-robot-contact-people')),
      icon: <UsersIcon />,
      onClick: () => navigateTabClickHandler(SphereRobotTabs.Users),
    });
    pages.push({
      open: queryTab === SphereRobotTabs.Settings,
      content: (
        <RobotSettingsPage webSoftwareVersion={robot?.softwareVersion?.webSoftwareVersion} />
      ),
      tooltip: t('title-robot-settings'),
      title: getTitle(t('title-robot-settings')),
      icon: <SettingsIcon />,
      onClick: () => navigateTabClickHandler(SphereRobotTabs.Settings),
    });
  }

  return (
    <React.Fragment>
      <MainContentBlock isOverlayContent onClose={closeOverlayClickHandler} pages={pages} />
    </React.Fragment>
  );
};

export default RobotPage;
